import { Badge } from "@intility/bifrost-react";
import type { AppType } from "../api/types";
import {
  APP_TYPE_CONFIG,
  clientKindLabel,
  purposeLabel,
} from "../lib/clientTypes";

// Bifrost's default theme maps attn to pink, brand to teal and chill to
// purple, so the type colours come from the design system, not our own CSS.
const TYPE_BADGE_STATE: Record<
  AppType,
  "attn" | "brand" | "chill" | "neutral"
> = {
  spa: "attn",
  api: "brand",
  worker: "chill",
  custom: "neutral",
};

// The Bifrost state (colour) of a client type, for anything that should match
// its badge: SPA pink, API teal, other service purple.
export function clientTypeState(
  appType: AppType,
): "attn" | "brand" | "chill" | "neutral" {
  return TYPE_BADGE_STATE[appType] ?? "neutral";
}

// With `purposeTag` the badge reads "SPA: Web Application": the type stays
// semibold (the badge default), the purpose is regular weight.
export function ClientTypeBadge({
  appType,
  purposeTag,
}: {
  appType: AppType;
  purposeTag?: string | null;
}) {
  const label = purposeTag ? purposeLabel(appType, purposeTag) : null;

  return (
    <Badge state={TYPE_BADGE_STATE[appType] ?? "neutral"}>
      {APP_TYPE_CONFIG[appType]?.label ?? appType}
      {label && <span style={{ fontWeight: 400 }}>: {label}</span>}
    </Badge>
  );
}

// The badge of a client in a list or a menu: it says the purpose (API, SPA, AI
// Agent, MCP, Worker) and takes the colour of the client type. A client with
// no purpose gets the default purpose of its type.
export function ClientPurposeBadge({
  appType,
  purposeTag,
}: {
  appType: AppType;
  purposeTag?: string | null;
}) {
  return (
    <Badge state={TYPE_BADGE_STATE[appType] ?? "neutral"}>
      {clientKindLabel(appType, purposeTag)}
    </Badge>
  );
}
